import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Shield, ArrowRight, Loader2, Lock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('employee');

  // Employee state
  const [phone, setPhone] = useState('');
  const [empPassword, setEmpPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // RM state
  const [rmIdentifier, setRmIdentifier] = useState('');
  const [rmPassword, setRmPassword] = useState('');
  const [rmLoading, setRmLoading] = useState(false);

  // Admin state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const handleEmployeeLogin = async () => {
    if (phone.length !== 10) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }
    if (!empPassword) {
      toast.error('Enter your password');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.employeeLogin(phone, empPassword);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Login successful!');
      navigate('/employee');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRMLogin = async () => {
    if (!rmIdentifier || !rmPassword) {
      toast.error('Enter phone/email and password');
      return;
    }
    setRmLoading(true);
    try {
      const res = await authAPI.rmLogin(rmIdentifier, rmPassword);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('RM login successful!');
      navigate('/rm');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setRmLoading(false);
    }
  };

  const handleAdminLogin = async () => {
    if (!email || !password) {
      toast.error('Enter email and password');
      return;
    }
    setAdminLoading(true);
    try {
      const res = await authAPI.adminLogin(email, password);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#f4f4f5] flex flex-col">
      {/* Header with logos */}
      <div className="bg-[#026536] pt-10 pb-16 px-5">
        <div className="max-w-sm mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 data-testid="app-title" className="text-2xl font-extrabold tracking-tight text-white">
                UPASTITH
              </h1>
              <p className="text-white/80 text-[11px] mt-0.5 font-semibold uppercase tracking-widest">The Field Tracking App</p>
              <p className="text-white/50 text-xs mt-1 font-medium">by Uplift Health Hubs</p>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="h-11 w-11 rounded-full bg-white/95 p-[2px] shadow-lg shadow-black/10 ring-2 ring-white/20">
                <img src="/logo-sampada.jpeg" alt="Sampada Uplift" data-testid="logo-sampada"
                  className="h-full w-full rounded-full object-cover" />
              </div>
              <div className="h-11 w-11 rounded-xl bg-white/95 p-[3px] shadow-lg shadow-black/10 ring-2 ring-white/20">
                <img src="/logo-uplift-mutuals.png" alt="Uplift Mutuals" data-testid="logo-uplift"
                  className="h-full w-full rounded-lg object-contain" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="flex-1 -mt-8 px-4 pb-8">
        <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="w-full rounded-none h-12 bg-[#f4f4f5]" data-testid="login-tabs">
              <TabsTrigger value="employee" className="flex-1 h-10 data-[state=active]:bg-white data-[state=active]:text-[#026536] font-semibold text-xs" data-testid="tab-employee">
                <Phone size={14} className="mr-1" /> Employee
              </TabsTrigger>
              <TabsTrigger value="rm" className="flex-1 h-10 data-[state=active]:bg-white data-[state=active]:text-[#026536] font-semibold text-xs" data-testid="tab-rm">
                <Users size={14} className="mr-1" /> RM
              </TabsTrigger>
              <TabsTrigger value="admin" className="flex-1 h-10 data-[state=active]:bg-white data-[state=active]:text-[#026536] font-semibold text-xs" data-testid="tab-admin">
                <Shield size={14} className="mr-1" /> Admin
              </TabsTrigger>
            </TabsList>

            {/* Employee Login */}
            <TabsContent value="employee" className="p-6 space-y-5 animate-fade-in">
              <div>
                <h2 className="text-lg font-bold text-[#1a2529]">Employee Sign In</h2>
                <p className="text-sm text-[#1a2529]/50 mt-0.5">Enter your mobile number and password</p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="phone" className="text-xs font-semibold text-[#1a2529]/70">Mobile Number</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="h-12 px-3 flex items-center bg-[#f4f4f5] rounded-md text-sm font-semibold text-[#1a2529]/50">+91</span>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="10-digit number"
                      className="h-12 bg-[#f4f4f5] border-0 text-base font-medium placeholder:text-[#1a2529]/30 flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="emp-password" className="text-xs font-semibold text-[#1a2529]/70">Password</Label>
                  <Input
                    id="emp-password"
                    data-testid="emp-password-input"
                    type="password"
                    value={empPassword}
                    onChange={(e) => setEmpPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="h-12 mt-1 bg-[#f4f4f5] border-0 text-base font-medium placeholder:text-[#1a2529]/30"
                    onKeyDown={(e) => e.key === 'Enter' && handleEmployeeLogin()}
                  />
                </div>
              </div>
              <Button
                data-testid="employee-login-btn"
                onClick={handleEmployeeLogin}
                disabled={loading || phone.length !== 10 || !empPassword}
                className="w-full h-12 bg-[#026536] hover:bg-[#026536]/90 text-white font-semibold text-base rounded-xl shadow-lg shadow-[#026536]/20 active:scale-[0.98] transition-all"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Lock size={16} className="mr-2" />}
                Sign In
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </TabsContent>

            {/* RM Login */}
            <TabsContent value="rm" className="p-6 space-y-5 animate-fade-in">
              <div>
                <h2 className="text-lg font-bold text-[#1a2529]">RM Sign In</h2>
                <p className="text-sm text-[#1a2529]/50 mt-0.5">Reporting Manager login</p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="rm-id" className="text-xs font-semibold text-[#1a2529]/70">Phone or Email</Label>
                  <Input
                    id="rm-id"
                    data-testid="rm-identifier-input"
                    type="text"
                    value={rmIdentifier}
                    onChange={(e) => setRmIdentifier(e.target.value)}
                    placeholder="Phone number or email"
                    className="h-12 mt-1 bg-[#f4f4f5] border-0 text-base font-medium placeholder:text-[#1a2529]/30"
                  />
                </div>
                <div>
                  <Label htmlFor="rm-password" className="text-xs font-semibold text-[#1a2529]/70">Password</Label>
                  <Input
                    id="rm-password"
                    data-testid="rm-password-input"
                    type="password"
                    value={rmPassword}
                    onChange={(e) => setRmPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="h-12 mt-1 bg-[#f4f4f5] border-0 text-base font-medium placeholder:text-[#1a2529]/30"
                    onKeyDown={(e) => e.key === 'Enter' && handleRMLogin()}
                  />
                </div>
              </div>
              <Button
                data-testid="rm-login-btn"
                onClick={handleRMLogin}
                disabled={rmLoading || !rmIdentifier || !rmPassword}
                className="w-full h-12 bg-[#dea439] hover:bg-[#dea439]/90 text-white font-semibold text-base rounded-xl shadow-lg shadow-[#dea439]/20 active:scale-[0.98] transition-all"
              >
                {rmLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Users size={16} className="mr-2" />}
                Sign In as RM
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </TabsContent>

            {/* Admin Login */}
            <TabsContent value="admin" className="p-6 space-y-5 animate-fade-in">
              <div>
                <h2 className="text-lg font-bold text-[#1a2529]">Admin Login</h2>
                <p className="text-sm text-[#1a2529]/50 mt-0.5">Sign in with admin credentials</p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="admin-email" className="text-xs font-semibold text-[#1a2529]/70">Email</Label>
                  <Input
                    id="admin-email"
                    data-testid="admin-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@company.com"
                    className="h-12 mt-1 bg-[#f4f4f5] border-0 text-base font-medium placeholder:text-[#1a2529]/30"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-password" className="text-xs font-semibold text-[#1a2529]/70">Password</Label>
                  <Input
                    id="admin-password"
                    data-testid="admin-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="h-12 mt-1 bg-[#f4f4f5] border-0 text-base font-medium placeholder:text-[#1a2529]/30"
                    onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                  />
                </div>
              </div>
              <Button
                data-testid="admin-login-btn"
                onClick={handleAdminLogin}
                disabled={adminLoading}
                className="w-full h-12 bg-[#1a2529] hover:bg-[#1a2529]/90 text-white font-semibold text-base rounded-xl shadow-lg active:scale-[0.98] transition-all"
              >
                {adminLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Sign In
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer branding */}
        <div className="flex items-center justify-center gap-2 mt-6 opacity-40">
          <img src="/logo-sampada.jpeg" alt="" className="h-5 w-5 rounded-full object-cover" />
          <span className="text-[10px] text-[#1a2529] font-medium">Sampada Uplift Mahila Nagari Sahakari Patsanstha</span>
        </div>
      </div>
    </div>
  );
}
